/**
 * 实时提示条的**纯逻辑层** —— 事件 → 该不该提示 / 提示什么 · 零 DOM · 零网络 · 可直测
 *
 * 依据：L 域 PRD **§5.2 口径 5**（增量提示）· **§7.2 `AC-L2.8`** ·
 * 架构 **§7.2**（客户端呈现策略）· `DR-018` §「增量提示」· `contract/realtime.ts` 文件头。
 *
 * ## 🔴 本批（`66`）闭合的欠账
 *
 * `53` 批接通实时同步时**只做了失效刷新**（数据正确性），呈现层留了个**已登记的空缺**：
 * 架构 §7.2 的"别人动了什么"提示。当时的登记原话是「需要「我的 userId」参与"忽略自己"，
 * 而 `Session` **没有** userId 字段」⇒ 本批先补 `Session.userId`（**搬运**，契约里一直有
 * `AuthUserView.id`），再实现提示条。
 *
 * ## 🔴 三条纪律
 *
 * | # | 纪律 | 怎么做的 |
 * |---|---|---|
 * | ① | 🔴 **端侧零口径** | 文案里的"N 笔""删了哪条"**全部来自事件载荷**，本文件**不算任何数**（不做 groupby、不做去重统计） |
 * | ② | 🔴 **忽略自己只认 `actorUserId`** | 与 `session.userId` **逐字段比对**（`===`）。❌ 不用 `userName`（展示名可重复 ⇒ 自弹）、❌ 不用"操作前后数量差"（那是第二份聚合） |
 * | ③ | 🔴 **`userId` 为空 ⇒ 一律当"别人"** | 老会话缺 `userId` 时（`''`）**不做"看起来像我"的猜测** —— 提示条是**协作感知**，漏报（别人改了我不说）比误报严重得多 |
 *
 * ## ⚠️ 本批**只做「顶部轻提示条」那一半**（诚实登记）
 *
 * `AC-L2.8` 的两个半边是：
 *
 * | 场景 | 行为 | 本批 |
 * |---|---|---|
 * | 用户**不在**流水页 | 顶部轻提示条「新增 N 笔 · 查看」 | ✅ **本批做** |
 * | 用户**在**流水页 | **静默插入 + 高亮 3 秒** | ❌ **未做**（见下） |
 * | 收到**删除**事件 | 移除该行 + 提示「该流水已被删除」 | ✅ 提示条这半边（移行属"在流水页"那半边） |
 *
 * 「静默插入 + 高亮 3 秒」不做，理由**不是**偷懒：它要求"高亮"是一个**跨 3 秒的时序状态**，
 * 而本项目的端侧测试基座（`react-dom/server` 静态渲染，**不跑 effect、无 layout engine**）**
 * **证不了任何时序/几何结论**（铁律 43）。硬做 = 写一堆**永远绿的空断言**（铁律 54①）。
 * ⇒ 该半边**显式留空并登记**，与 `AC-N4.21` 同一种守法：能做的一边做掉，做不了的一边写清
 * "为什么现在做不了"（铁律 46②）。它需要的是**无头浏览器取证**（同 `㊷` 批的处置）。
 */

import { RealtimeEvent, type RealtimeAction, type RealtimeEventMap } from '@jiaying/shared';

// ───────────────────────── 输入：一条到达的事件 ─────────────────────────

/** 端侧收到的单条事件（事件名 + 载荷 —— 与 `useRealtime` 的 `socket.on` 回调同形） */
export interface ToastEvent {
  readonly event: keyof RealtimeEventMap;
  readonly payload: RealtimeEventMap[keyof RealtimeEventMap];
}

/** 判断所需的最小上下文（**只这两项** —— 都是会话里的字段，不引入任何算出来的值） */
export interface ToastContext {
  /** 当前账本 id（`Session.ledgerId`） */
  readonly ledgerId: string;
  /** 本人 `users.id`（`Session.userId`；老会话为 `''`） */
  readonly userId: string;
}

/**
 * 这条事件**是否该弹提示条**。
 *
 * ## 🔴 三道闸，逐条对应一条契约事实
 *
 * 1. **账本闸**：`payload.ledgerId !== ctx.ledgerId` ⇒ 丢。
 *    理由与 `useRealtime` 的失效闸**同源**（契约原文）：切账本后上一本的在途事件会**晚到**，
 *    不丢就会把 A 账本的变化说成 B 账本的（"看起来像数据错乱"的静默缺陷）。
 * 2. **自己闸**：`payload.actorUserId === ctx.userId` ⇒ 丢（自己刚做的操作不提醒自己）。
 *    ⚠️ `ctx.userId === ''`（老会话）时本闸**永不命中** ⇒ 一律当"别人"（纪律 ③）。
 * 3. **可提示闸**：只有**有明确动作语义**的事件才提示 —— 见 {@link toastText}。
 *    ③ 预算事件**当前无发射方**（= H 域，契约文件头已登记）⇒ 不在这里编文案（编了就是
 *    给一个不存在的事件造第二份口径）；它的文案等 H 域落地时**同批**加（铁律 53）。
 */
export function shouldToast(ev: ToastEvent, ctx: ToastContext): boolean {
  if (ev.payload.ledgerId !== ctx.ledgerId) return false;
  if (ctx.userId !== '' && ev.payload.actorUserId === ctx.userId) return false;
  return toastText(ev) !== null;
}

// ───────────────────────── 输出：提示什么 ─────────────────────────

/**
 * 事件 → **一句中文提示**；不可提示（如成员事件）返回 `null`。
 *
 * ## 🔴 为什么成员事件不弹
 *
 * 成员变动（加入/退出/被移除）**已经有专门的位置**：顶栏头像堆叠 + 设置页「成员」面板
 * （`listLedgerMembers()` 是它们**唯一**的数据源，`AC-L6.1`）。再弹一条"王琳加入了账本"
 * 会造出**第二个告知渠道**，两处迟早不一致（铁律 24）。
 * ⇒ 成员事件**照旧只做失效刷新**（`['members']` / `['ledger']` / `['accounts']`），不提示。
 *
 * ## 🔴 动作 → 文案的映射**只此一份**
 *
 * `RealtimeAction` 是契约里的闭集（`created` / `updated` / `deleted` / `restored`）。
 * 本函数用 `switch` **穷举**它 —— 将来契约加动作，这里**编译报错**（比"漏一个动作、
 * 用户看到空白提示条"好）。文案本身在 `realtime-toast-labels.ts`（本项目一贯做法：
 * 文案与逻辑分开，spec 能**对字**断言）。
 *
 * ## ⚠️ 为什么不带"是谁改的"
 *
 * 载荷里**只有 `actorUserId`，没有姓名**（契约刻意不带业务字段 —— `contract/realtime.ts`
 * 文件头的理由：带业务字段会出现第二份行装配实现）。端侧要显示姓名就得**再取一次成员名单**，
 * 那是一份"事件 → 姓名"的反查，且**新成员/改名的时序**会让它短暂对不上。
 * ⇒ 本批的提示条**只说事实（谁动了什么不算），只说"发生了什么"**。
 * 要"王琳改了 1 笔"这种带名字的形态，需要**服务端在载荷里带 `actorName`**（契约变更，等拍板）。
 */
export function toastText(ev: ToastEvent): string | null {
  switch (ev.event) {
    case RealtimeEvent.Txn: {
      const p = ev.payload as RealtimeEventMap[typeof RealtimeEvent.Txn];
      return txnToastText(p.action);
    }
    case RealtimeEvent.Account: {
      const p = ev.payload as RealtimeEventMap[typeof RealtimeEvent.Account];
      // 🔴 转账是"账户类自带语义"（契约原文）—— 文案必须区分，否则用户看到"账户有变化"
      //    却不知道钱去哪了（一笔转账动两个账户，说"新增了 1 个账户"是错的）
      return accountToastText(p.action, p.viaTransfer === true);
    }
    case RealtimeEvent.Budget:
      // ③ 发射方 = H 域（未落地）⇒ 尚无可提示的语义，**不编文案**（见上方 shouldToast 说明）
      return null;
    case RealtimeEvent.Member:
      // ④ 有专门的告知位置 ⇒ 不在这里重复（见上方文件注释）
      return null;
  }
}

/** 流水事件文案（穷举动作 ⇒ 契约加动作时此处编译报错） */
function txnToastText(action: RealtimeAction): string {
  switch (action) {
    case 'created':
      return '新增 1 笔交易';
    case 'updated':
      return '有 1 笔交易被修改';
    case 'deleted':
      return '有 1 笔交易被删除';
    case 'restored':
      return '有 1 笔交易被恢复';
  }
}

/** 账户事件文案 —— 🔴 转账单独成句（它动的是**两个**账户，不说"新增账户"） */
function accountToastText(action: RealtimeAction, viaTransfer: boolean): string {
  if (viaTransfer) return '有一笔转账入账';
  switch (action) {
    case 'created':
      return '新增 1 个账户';
    case 'updated':
      return '有 1 个账户被修改';
    case 'deleted':
      return '有 1 个账户被删除';
    case 'restored':
      return '有 1 个账户被恢复';
  }
}

// ───────────────────────── 合并：连到来时只留一条 ─────────────────────────

/**
 * 把**连续到达**的同类事件合并成一条提示（如另一端批量导入 20 笔 ⇒ "新增 20 笔交易"）。
 *
 * ## 🔴 为什么合并发生在**这个纯函数**里，而不是在组件里攒
 *
 * 组件里攒 = 组件里算数（违反纪律 ①"端侧零口径"），而且**攒的逻辑与时序耦合**
 * （定时器窗口），静态渲染**测不了**（铁律 43）。
 * ⇒ 组件只负责"收到一个 `ToastState` 就渲染"；**合并规则在这里，可直测**。
 *
 * ## ⚠️ 合并**只合并完全同类**的相邻事件
 *
 * 判据 = `event` + `action` + `viaTransfer` 三者全同。为什么这么严：`updated` 与 `deleted`
 * 混在一起说"21 笔交易有变化"会**掩盖"其中 1 笔被删了"**这个用户必须知道的事实
 * （`AC-L2.6`：删除优先）。⇒ 宁可两条轮播，不做"看起来更简洁"的合并。
 *
 * @param prev 已有提示（`null` = 当前无提示）
 * @param next 新到事件的文案与同类键
 * @returns 合并后的提示
 */
export interface ToastState {
  /** 合并后的文案（含计数） */
  readonly text: string;
  /** 合并键（`event|action|viaTransfer`）—— 只有同键才累加 */
  readonly key: string;
  /** 累计条数（≥ 1） */
  readonly count: number;
}

/** 事件的**合并键**（⚠️ 与 `toastText` 的文案分派必须覆盖同一批事件 —— 见 spec 成对断言） */
export function toastKey(ev: ToastEvent): string | null {
  const anyPayload = ev.payload as { action?: unknown; viaTransfer?: unknown };
  const action = typeof anyPayload.action === 'string' ? anyPayload.action : '';
  if (toastText(ev) === null) return null;
  const transfer = anyPayload.viaTransfer === true ? 'transfer' : '';
  return `${ev.event}|${action}|${transfer}`;
}

/** 文案的**复数形态**：`count > 1` 时要带数字（只说"新增 1 笔"会让 20 笔的导入看着像 1 笔） */
export function countedText(base: string, count: number): string {
  if (count <= 1) return base;
  // 把基数词换成合并数（文案模板统一写"1"，这里替换 —— 避免为每个动作再写一份复数模板）
  return base.replace('1 ', `${String(count)} `).replace('1 个', `${String(count)} 个`).replace('1 笔', `${String(count)} 笔`);
}

/**
 * 合并一个**已通过 `shouldToast` 的**事件到当前提示。
 *
 * @param prev 当前提示（`null` = 没有）
 * @param ev 新事件（调用方**必须**先用 `shouldToast` 过闸 —— 本函数不再判归属，
 *           避免"两道闸分散在两处"这种最容易漂移的写法）
 */
export function mergeToast(prev: ToastState | null, ev: ToastEvent): ToastState | null {
  const text = toastText(ev);
  const key = toastKey(ev);
  if (text === null || key === null) return prev;
  if (prev !== null && prev.key === key) {
    const count = prev.count + 1;
    return { text: countedText(text, count), key, count };
  }
  return { text, key, count: 1 };
}
